import { useEffect, useState } from "react";
import NavbarTop from "../../components/NavbarTop";
import "./style.css"
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useMessage } from "../../global-components/src";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { Post } from "../../types";
import { Link } from "react-router-dom";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { User } from "../../global-components/src/types";
import { useTranslation } from 'react-i18next';

type DashboardType = {
    userCount: number;
    postCount: number;
    commentCount: number;
    groupCount: number;
    storyCount: number;
    activeUsersToday: number;
    activeUsersMonth: number;
    weeklyRankingFirst: {
        points: number;
        user: User;
    } | null;
    mostFollowedProfile: {
        user_name: string;
        total_followers: number;
    };
    mostLikedPost: {
        id: number;
        total_likes: number;
        post: Post;
        user: User;
    };
    mostCommentedPost: {
        id: number;
        total_comments: number;
        post: Post;
        user: User;
    };
    mostReplyPost: {
        id: number;
        total_shares: number;
        post: Post;
        user: User;
    };
    mostActiveStreak: {
        current_day: number;
        user: User;
    };
}

interface StatCardProps {
    icon: string;
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    loading: boolean;
    accent?: boolean;
}

const StatCard = ({ icon, label, value, sub, loading, accent }: StatCardProps) => (
    <div className={`dash-card${accent ? " dash-card--accent" : ""}`}>
        <div className="dash-card__icon">{icon}</div>
        <div className="dash-card__body">
            {loading ? (
                <Spinner size="32px" />
            ) : (
                <>
                    <div className="dash-card__value">{value}</div>
                    {sub && <div className="dash-card__sub">{sub}</div>}
                </>
            )}
            <div className="dash-card__label">{label}</div>
        </div>
    </div>
);

const Dashboard = () => {
    const [loading, setLoading] = useState(false);
    const [dashboard, seDashboard] = useState<DashboardType | null>(null);
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    
    useEffect(() => {
        loadData()
    }, []);
    
    const loadData = async () => {
        const url = `${PACOCA_API_URL}/public/dashboard`;

        if (loading) return;
        setLoading(true);

        try {
            const response = await axios.post(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                }
            });

            seDashboard(response.data);
        } catch (err: any) {
            if (err.status === 429 && err.message.blocked)
                setMessage(t("generics.errors.tooManyRequests", { minutes: err.remaining }), "error");
            else if (err.status === 429)
                setMessage(t("generics.errors.tooManyRequestsGeneric"), "error");
            else if (err.response) {
                console.error("Erro no servidor:", err.response.status, err.response.data);
                setMessage(err.response.data.message || "Erro ao carregar dados!", "error");
            } else if (err.request) {
                console.error("Nenhuma resposta do servidor:", err.request);
                setMessage(t("generics.errors.genericServerError"), "error");
            } else {
                console.error("Erro inesperado:", err.message);
                setMessage("Ocorreu um erro inesperado!", "error");
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Helmet>
                <title>Paçoca - Dashboard</title>
                <meta name="description" content="Paçoca - veja os dados do paçoca, como quantidade de usuários, perfil mais seguido, post mais curtido, etc"/>
                <meta name="keywords" content="Paçoca, rede social brasileira, Dashboard, dados, informações"/>
                <meta name="author" content="Paçoca Inc."/>
                <meta property="og:title" content="Paçoca - Dashboard"/>
                <meta property="og:description" content="Paçoca - veja os dados do paçoca, como quantidade de usuários, perfil mais seguido, post mais curtido, etc."/>
                <meta property="og:image" content="/img/link-preview.png?v=1" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={window.location.href}/>
                <meta property="og:type" content="website"/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:title" content="Paçoca - Dashboard"/>
                <meta name="twitter:description" content="Paçoca - veja os dados do paçoca, como quantidade de usuários, perfil mais seguido, post mais curtido, etc"/>
                <meta name="twitter:image" content="/img/link-preview.png?v=1" />
            </Helmet>

            <NavbarTop/>
            <div className="dash-page container">
                <div className="dash-header">
                    <h1 className="dash-title">{t("dashboard.title")}</h1>
                    <p className="dash-subtitle">{t("dashboard.subtitle")}</p>
                </div>

                {/* ── Visão Geral ────────────────────────────────── */}
                <section className="dash-section">
                    <h2 className="dash-section__title">{t("dashboard.sectionOverview")}</h2>
                    <div className="dash-grid dash-grid--3">
                        <StatCard
                            icon="👥"
                            label={t("dashboard.registeredUsers")}
                            value={<span className="dash-number">{dashboard?.userCount?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="📝"
                            label={t("dashboard.publishedPosts")}
                            value={<span className="dash-number">{dashboard?.postCount?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="💬"
                            label={t("dashboard.comments")}
                            value={<span className="dash-number">{dashboard?.commentCount?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="🏘️"
                            label={t("dashboard.communities")}
                            value={<span className="dash-number">{dashboard?.groupCount?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="📸"
                            label={t("dashboard.storiesPosted")}
                            value={<span className="dash-number">{dashboard?.storyCount?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="🟢"
                            label={t("dashboard.activeUsersToday")}
                            value={<span className="dash-number">{dashboard?.activeUsersToday?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                        <StatCard
                            icon="📅"
                            label={t("dashboard.activeUsersMonth")}
                            value={<span className="dash-number">{dashboard?.activeUsersMonth?.toLocaleString("pt-BR")}</span>}
                            loading={loading}
                        />
                    </div>
                </section>

                {/* ── Destaques ──────────────────────────────────── */}
                <section className="dash-section">
                    <h2 className="dash-section__title">{t("dashboard.sectionHighlights")}</h2>
                    <div className="dash-grid dash-grid--2">
                        <StatCard
                            icon="🏆"
                            label={t("dashboard.weeklyRankingFirst")}
                            accent
                            value={
                                dashboard?.weeklyRankingFirst?.user
                                    ? <Link className="dash-link" to={`/@${dashboard.weeklyRankingFirst.user.user_name}`}>
                                        @{dashboard.weeklyRankingFirst.user.user_name}
                                    </Link>
                                    : <span>—</span>
                            }
                            sub={
                                dashboard?.weeklyRankingFirst?.points
                                    ? <span className="dash-sub-stat">{dashboard.weeklyRankingFirst.points.toLocaleString("pt-BR")} {t("dashboard.points")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                        <StatCard
                            icon="⭐"
                            label={t("dashboard.mostFollowedProfile")}
                            accent
                            value={
                                <Link className="dash-link" to={`/@${dashboard?.mostFollowedProfile?.user_name}`}>
                                    @{dashboard?.mostFollowedProfile?.user_name}
                                </Link>
                            }
                            sub={
                                dashboard?.mostFollowedProfile?.total_followers
                                    ? <span className="dash-sub-stat">{dashboard.mostFollowedProfile.total_followers.toLocaleString("pt-BR")} {t("dashboard.followers")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                        <StatCard
                            icon="🔥"
                            label={t("dashboard.longestStreak")}
                            accent
                            value={
                                dashboard?.mostActiveStreak?.user
                                    ? <Link className="dash-link" to={`/@${dashboard.mostActiveStreak.user.user_name}`}>
                                        @{dashboard.mostActiveStreak.user.user_name}
                                    </Link>
                                    : <span>—</span>
                            }
                            sub={
                                dashboard?.mostActiveStreak?.current_day
                                    ? <span className="dash-sub-stat">{dashboard.mostActiveStreak.current_day} {t("dashboard.consecutiveDays")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                        <StatCard
                            icon="❤️"
                            label={t("dashboard.mostLikedPost")}
                            accent
                            value={
                                <Link className="dash-link" to={`/post/${dashboard?.mostLikedPost?.id}`}>
                                    @{dashboard?.mostLikedPost?.user?.user_name}
                                </Link>
                            }
                            sub={
                                dashboard?.mostLikedPost?.total_likes
                                    ? <span className="dash-sub-stat">{dashboard.mostLikedPost.total_likes.toLocaleString("pt-BR")} {t("dashboard.likes")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                        <StatCard
                            icon="🗨️"
                            label={t("dashboard.mostCommentedPost")}
                            accent
                            value={
                                <Link className="dash-link" to={`/post/${dashboard?.mostCommentedPost?.id}`}>
                                    @{dashboard?.mostCommentedPost?.user?.user_name}
                                </Link>
                            }
                            sub={
                                dashboard?.mostCommentedPost?.total_comments
                                    ? <span className="dash-sub-stat">{dashboard.mostCommentedPost.total_comments.toLocaleString("pt-BR")} {t("dashboard.commentsCount")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                        <StatCard
                            icon="🔁"
                            label={t("dashboard.mostSharedPost")}
                            accent
                            value={
                                <Link className="dash-link" to={`/post/${dashboard?.mostReplyPost?.id}`}>
                                    @{dashboard?.mostReplyPost?.user?.user_name}
                                </Link>
                            }
                            sub={
                                dashboard?.mostReplyPost?.total_shares
                                    ? <span className="dash-sub-stat">{dashboard.mostReplyPost.total_shares.toLocaleString("pt-BR")} {t("dashboard.shares")}</span>
                                    : null
                            }
                            loading={loading}
                        />
                    </div>
                </section>
            </div>
        </>
    );
};

export default Dashboard;
